import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartOptions } from 'chart.js'
import { Typography, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import NoResults from '@/assets/svg/no-results.svg?react' // Ensure this path is correct

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

export type BarChartComponentProps = {
  data: Array<{ label: string; values: Array<{ subLabel: string; value: number }> }>
  title: string
  config?: ChartOptions<'bar'> // Additional configuration or styling options if necessary
}

function BarChartComponent({ data, title, config }: BarChartComponentProps) {
  const { t } = useTranslation()

  const { labels, datasets } = useMemo(() => {
    // First, determine all unique dates across all datasets to ensure we cover the entire range
    const uniqueDates = Array.from(new Set(data.flatMap((item) => item.values.map((val) => val.subLabel)))).sort()
    const colorPalette = [
      'rgba(255, 99, 132, 0.5)', // For label 1
      'rgba(54, 162, 235, 0.5)', // For label 2
      'rgba(255, 206, 86, 0.5)', // For label 3
      // ...add more colors for each label
    ]

    // Map each type (label) to its own dataset
    const datasets = data.map((item, index) => {
      // Map each unique date to the corresponding value or 0 if no value is present
      const dataForDates = uniqueDates.map((date) => {
        const valueObject = item.values.find((val) => val.subLabel === date)
        return valueObject ? valueObject.value : 0
      })

      return {
        label: t(item.label),
        data: dataForDates,
        backgroundColor: colorPalette[index % colorPalette.length],
      }
    })

    return {
      labels: uniqueDates.map((date) => t(date)), // All dates on the x-axis
      datasets, // One dataset for each label
    }
  }, [data, t])

  const options = useMemo(
    () => ({
      responsive: true,
      scales: {
        x: {
          stacked: true, // Use this if you want stacked bars
        },
        y: {
          stacked: true, // Use this if you want stacked bars
        },
      },
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: title,
        },
      },
      ...config,
    }),
    [title, config]
  )

  const hasData = datasets.some((ds) => ds.data.length > 0)

  return (
    <Box>
      <Typography variant="h2">{title}</Typography>
      {hasData ? (
        <Bar data={{ labels, datasets }} options={options} />
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px" width="100%">
          <NoResults style={{ maxWidth: '40%', height: 'auto' }} />
        </Box>
      )}
    </Box>
  )
}

export default BarChartComponent
